import { useMutation, useQueryClient, type UseMutateFunction } from '@tanstack/react-query';
import { companyUserService } from '../services/company-user.service';
import type { CompanyUser, CreateCompanyUserRequest } from '../types/company-admin.types';
import { useAuthStore } from '@/features/auth/stores/authStore';

interface CreateCompanyUserParams {
  request: CreateCompanyUserRequest;
}

export function useCreateCompanyUser(): {
  createUser: UseMutateFunction<CompanyUser, Error, CreateCompanyUserParams>;
  createUserAsync: (params: CreateCompanyUserParams) => Promise<unknown>;
  isPending: boolean;
  isSuccess: boolean;
  isError: boolean;
  error: Error | null;
  reset: () => void;
} {
  const queryClient = useQueryClient();
  const getEffectiveCompanyId = useAuthStore((state) => state.getEffectiveCompanyId);

  const mutation = useMutation({
    mutationFn: ({ request }: CreateCompanyUserParams) => {
      const companyId = getEffectiveCompanyId();
      if (!companyId) {
        throw new Error('No company context available');
      }
      return companyUserService.createUser(request, companyId);
    },
    onSuccess: () => {
      const companyId = getEffectiveCompanyId();
      queryClient.invalidateQueries({ queryKey: ['company-users', companyId] });
    },
  });

  return {
    createUser: mutation.mutate,
    createUserAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
    reset: mutation.reset,
  };
}
